import { ContentSwitcher, IconButton, TextInput } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { TrainingScheduleOperationsItem } from "@/api/trainingSchedule";
import {
  CronRecurrenceFields,
  GatedToggle,
  MinFreeGpusField,
  ScheduleDateTimePicker,
} from "@/page/training/submit/scheduleFields";
import { SubmitField } from "@/page/training/submit/submitFields";
import {
  asScheduleDraft,
  branchFollowGate,
  trackLatestEvalGate,
  type ScheduleEditDraft,
} from "./scheduleEdit";
import styles from "./ScheduleEditDialog.module.css";

/**
 * Schedule 편집 다이얼로그의 **입력** 영역. PATCH 로 보낼 수 있는 축만 그린다.
 *
 * 무엇을 보낼지는 `scheduleEdit` 의 `buildSchedulePatch` 가 소유한다. 여기서는 그 축의 입력만
 * 연결한다. 실행 게이트를 켤 수 있는지와 켤 수 없는 사유도 `scheduleEdit` 이 판정한다.
 */

export function EditFields({
  item,
  draft,
  updateDraft,
  recurrenceWarning,
  totalGpus,
}: {
  item: TrainingScheduleOperationsItem;
  draft: ScheduleEditDraft;
  updateDraft: (patch: Partial<ScheduleEditDraft>) => void;
  recurrenceWarning: boolean;
  totalGpus: number | null;
}): ReactNode {
  // 켤 수 있는지와 켤 수 없는 사유는 `scheduleEdit` 이 소유한다. recipe 요약 부재를 "Commit 고정"·
  // "Evaluation 없음"과 같은 표기로 합치면 사실이 아닌 사유가 표시된다(§16).
  const branchFollow = branchFollowGate(item);
  const trackLatestEval = trackLatestEvalGate(item);
  // 표시 전용. `GatedToggle` 은 잠긴 토글의 hint 를 그리지 않는다. 그래서 이 값은 게이트가 열린
  // 때만 쓰이고, 그때 ref 는 항상 branch 다.
  const branchRef = item.schedule_recipe.learning_core?.ref ?? "";
  const endAtSet = item.end_at != null;
  return (
    <div className={styles.editFields}>
      <SubmitField label="Schedule 이름" required>
        <TextInput
          value={draft.name}
          aria-label="Schedule 이름"
          onChange={(event) => updateDraft({ name: event.target.value })}
        />
      </SubmitField>
      <SubmitField label="설명">
        <TextInput
          value={draft.description}
          aria-label="설명"
          onChange={(event) => updateDraft({ description: event.target.value })}
        />
      </SubmitField>
      <SubmitField
        label="반복 방식"
        hint={
          recurrenceWarning
            ? "반복 규칙을 바꾸면 다음 실행 시각을 지금 기준으로 다시 정합니다."
            : undefined
        }
      >
        <ContentSwitcher
          label="반복 방식"
          size="sm"
          options={[
            { value: "interval", label: "간격" },
            { value: "cron", label: "cron" },
          ]}
          value={draft.mode}
          onValueChange={(value) => updateDraft({ mode: value === "cron" ? "cron" : "interval" })}
        />
      </SubmitField>
      {draft.mode === "interval" ? (
        <SubmitField label="반복 간격 (분)">
          <TextInput
            value={draft.intervalMinutes}
            inputMode="numeric"
            aria-label="반복 간격 (분)"
            onChange={(event) => updateDraft({ intervalMinutes: event.target.value })}
          />
        </SubmitField>
      ) : (
        <CronRecurrenceFields
          cron={draft.cron}
          timezone={draft.timezone}
          confirmVerb="저장할 때"
          onChange={updateDraft}
        />
      )}
      <SubmitField
        label="종료 시각"
        // 상류는 값 변경은 받고 null 은 무시한다. 바꿀 수는 있지만 지울 수는 없다는 사실을 힌트가 알린다.
        hint={
          endAtSet
            ? "한 번 정한 종료 시각은 바꿀 수는 있지만 지울 수는 없습니다."
            : "비우면 삭제할 때까지 반복합니다."
        }
      >
        <div className={styles.endAtRow}>
          <ScheduleDateTimePicker
            value={draft.endAt}
            ariaLabel="종료 시각"
            onChange={(endAt) => updateDraft({ endAt })}
          />
          {/* 피커에는 지우기가 없다. 아직 서버에 없는 값만 되돌릴 수 있으므로 그때만 버튼을 둔다. */}
          {!endAtSet && draft.endAt !== "" ? (
            <IconButton
              text="지우기"
              size="sm"
              variant="ghost"
              onClick={() => updateDraft({ endAt: "" })}
            />
          ) : null}
        </div>
      </SubmitField>
      <GatedToggle
        label="새 Commit이 있을 때만 실행"
        hint={`Branch(${branchRef})의 최신 Commit이 직전 실행과 같으면 건너뜁니다.`}
        hintPlacement="below"
        available={branchFollow.available}
        lockedReason={branchFollow.lockedReason}
        checked={draft.skipIfUnchanged}
        onChange={(checked) => updateDraft({ skipIfUnchanged: checked })}
      />
      <MinFreeGpusField
        draft={asScheduleDraft(draft)}
        totalGpus={totalGpus}
        clearsExisting={item.min_free_gpus != null}
        onChange={(minFreeGpus) => updateDraft({ minFreeGpus })}
      />
      <GatedToggle
        label="Evaluation Image를 항상 최신 Release로 씁니다"
        available={trackLatestEval.available}
        lockedReason={trackLatestEval.lockedReason}
        checked={draft.trackLatestEvalRelease}
        onChange={(checked) => updateDraft({ trackLatestEvalRelease: checked })}
      />
    </div>
  );
}
